import { Transaction, TransactionInput, PayeeSuggestion } from '../types';
import { PublicClientApplication } from '@azure/msal-browser';
import { tokenRequest } from '../auth/authConfig';

const API_BASE = '/api';
let msalInstance: PublicClientApplication | null = null;

export function setMsalInstance(instance: PublicClientApplication) {
  msalInstance = instance;
}

async function getToken(): Promise<string> {
  if (!msalInstance) throw new Error('MSAL not initialized');

  // Ensure MSAL is initialized
  await msalInstance.initialize();

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    // No cached account, so send the user through interactive sign-in.
    await msalInstance.acquireTokenRedirect(tokenRequest);
    throw new Error('Redirecting for authentication...');
  }

  try {
    const response = await msalInstance.acquireTokenSilent({
      ...tokenRequest,
      account: accounts[0],
    });
    return response.accessToken;
  } catch {
    // Silent renewal failed, almost always an expired session (the SPA
    // refresh token tops out around 24h). A full-page redirect is the only
    // reliable fallback here: this runs from an auto-firing data fetch, not a
    // user click, so acquireTokenPopup gets blocked by the browser and the app
    // silently renders an empty "no accounts" state. The redirect bounces
    // through Entra and returns with a fresh token (handleRedirectPromise in
    // index.tsx completes the round-trip).
    await msalInstance.acquireTokenRedirect({
      ...tokenRequest,
      account: accounts[0],
    });
    throw new Error('Redirecting for authentication...');
  }
}

/** Force interactive re-auth via full-page redirect (used when the server
 * rejects our token with a 401). Throws so callers stop processing while the
 * browser navigates away. */
async function reauthRedirect(): Promise<never> {
  if (msalInstance) {
    const accounts = msalInstance.getAllAccounts();
    await msalInstance.acquireTokenRedirect({
      ...tokenRequest,
      account: accounts[0],
    });
  }
  throw new Error('Session expired. Redirecting to sign in...');
}

async function request<T>(url: string, options?: RequestInit): Promise<T> {
  const token = await getToken();
  const res = await fetch(`${API_BASE}${url}`, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    ...options,
  });
  if (res.status === 401) {
    // Our token was rejected server-side (expired/invalid as seen by the API).
    // Re-authenticate interactively rather than surfacing a generic error that
    // would render an empty app.
    await reauthRedirect();
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || (err.errors ? JSON.stringify(err.errors) : res.statusText));
  }
  return res.json();
}

export interface AccountData {
  id: number;
  user_id: number;
  name: string;
  starting_balance: string;
  private: boolean;
  owner_name?: string;
  owner_email?: string;
  access_type?: string;
  created_at: string;
  updated_at: string;
}

export const api = {
  // User
  getMe: () => request<any>('/users/me'),

  // Accounts
  getAccounts: () => request<AccountData[]>('/accounts'),
  createAccount: (data: { name: string; starting_balance?: number }) =>
    request<AccountData>('/accounts', { method: 'POST', body: JSON.stringify(data) }),
  updateAccount: (id: number, data: Partial<{ name: string; starting_balance: number }>) =>
    request<AccountData>(`/accounts/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteAccount: (id: number) =>
    request<{ deleted: boolean }>(`/accounts/${id}`, { method: 'DELETE' }),

  // Transactions (scoped to account)
  getTransactions: (accountId: number) =>
    request<Transaction[]>(`/accounts/${accountId}/transactions`),
  createTransaction: (accountId: number, data: TransactionInput) =>
    request<Transaction>(`/accounts/${accountId}/transactions`, { method: 'POST', body: JSON.stringify(data) }),
  updateTransaction: (accountId: number, id: number, data: TransactionInput) =>
    request<Transaction>(`/accounts/${accountId}/transactions/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  toggleCleared: (accountId: number, id: number) =>
    request<Transaction>(`/accounts/${accountId}/transactions/${id}/clear`, { method: 'PATCH' }),
  deleteTransaction: (accountId: number, id: number) =>
    request<{ deleted: boolean; id: number }>(`/accounts/${accountId}/transactions/${id}`, { method: 'DELETE' }),

  // Payees
  searchPayees: (accountId: number, q: string) =>
    request<PayeeSuggestion[]>(`/accounts/${accountId}/payees?q=${encodeURIComponent(q)}`),

  // Sharing
  getShares: (accountId: number) => request<any[]>(`/accounts/${accountId}/shares`),
  addShareByEmail: (accountId: number, email: string) =>
    request<any>(`/accounts/${accountId}/shares`, { method: 'POST', body: JSON.stringify({ email }) }),
  removeShare: (accountId: number, userId: number) =>
    request<any>(`/accounts/${accountId}/shares/${userId}`, { method: 'DELETE' }),

  // CSV Import
  downloadTemplate: async (accountId: number) => {
    const token = await getToken();
    const res = await fetch(`${API_BASE}/accounts/${accountId}/transactions/template`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to download template');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'balancescript-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  },
  exportTransactions: async (accountId: number, accountName: string) => {
    const token = await getToken();
    const res = await fetch(`${API_BASE}/accounts/${accountId}/transactions/export`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to export transactions');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const safeName = accountName.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
    a.download = `balancescript-${safeName}-export.csv`;
    a.click();
    URL.revokeObjectURL(url);
  },
  previewImport: (accountId: number, csv: string) =>
    request<{ rows: TransactionInput[]; errors: Record<string, Record<string, string>>; valid: boolean }>(
      `/accounts/${accountId}/transactions/import`,
      { method: 'POST', body: JSON.stringify({ csv }) }
    ),
  confirmImport: (accountId: number, rows: TransactionInput[]) =>
    request<{ imported: number; transactions: Transaction[] }>(
      `/accounts/${accountId}/transactions/import/confirm`,
      { method: 'POST', body: JSON.stringify({ rows }) }
    ),

  // Users (admin)
  getUsers: () => request<any[]>('/users'),
  getInviteStatus: () => request<{ configured: boolean }>('/users/invite-status'),
  inviteUser: (email: string, displayName?: string) =>
    request<{ message: string; email: string; status: string }>('/users/invite', {
      method: 'POST', body: JSON.stringify({ email, displayName }),
    }),
  deleteUser: (id: number) =>
    request<{ deleted: boolean; id: number }>(`/users/${id}`, { method: 'DELETE' }),

  // Insights
  getInsights: (accountId: number) => request<any>(`/accounts/${accountId}/insights`),
};
